import MathManager from '@/utils/math-manager.utils'

class TransactionStatusManager {
  defaultObject = {
    value: 0,
    quantity: 0,
    quantityFormatted: 0,
    percentage: 0,
    tpv: 0
  }

  constructor (statusList, totalQuantity) {
    this.statusList = statusList
    this.totalQuantity = totalQuantity
  }

  #parseLabel (type) {
    const types = {
      awaitingPayment: 'awaitingPayment',
      authorized: 'authorized',
      inReview: 'inReview',
      reversalPending: 'reversalPending',
      processing: 'processing',
      revisionPending: 'revisionPending'
    }

    return types[type] ?? type
  }

  paid () {
    const paid = this.filterByStatus(['paid'])
    if (paid) return this.formatStatusResponse(paid)
    return this.formatStatusResponse(this.defaultObject)
  }

  pending () {
    const pending = this.filterByStatus(['pending'])
    if (pending) return this.formatStatusResponse(pending)
    return this.formatStatusResponse(this.defaultObject)
  }

  canceled () {
    const canceled = this.filterByStatus(['canceled'])
    if (canceled) return this.formatStatusResponse(canceled)
    return this.formatStatusResponse(this.defaultObject)
  }

  failed () {
    const failed = this.filterByStatus(['failed'])
    if (failed) return this.formatStatusResponse(failed)
    return this.formatStatusResponse(this.defaultObject)
  }

  others () {
    const othersFiltered = this.filterByStatusOthers([
      'paid',
      'pending',
      'canceled',
      'failed'
    ])

    const othersSummed = othersFiltered.reduce(
      (acc, curr) => {
        acc.quantity = acc.quantity + curr.quantity
        acc.tpv = acc.tpv + curr.tpv

        return { ...acc, name: curr.name }
      },
      {
        quantity: 0,
        tpv: 0
      }
    )

    const othersSummedFormated = this.formatStatusResponse(othersSummed)

    if (othersFiltered.length) {
      return {
        ...othersSummedFormated,
        tooltip: othersFiltered.map(el => this.formatStatusResponse(el))
      }
    }
    return this.formatStatusResponse(this.defaultObject)
  }

  filterByStatus (arrayStatus) {
    return this.mergeStatus(this.statusList).filter(item =>
      arrayStatus.includes(item.name)
    )[0]
  }

  filterByStatusOthers (arrayStatus) {
    return this.mergeStatus(this.statusList).filter(
      item => !arrayStatus.includes(item.name)
    )
  }

  formatStatusResponse (objStatus) {
    return {
      name: this.#parseLabel(objStatus.name),
      value: MathManager.formatCurrencyBRL(objStatus.tpv),
      quantity: objStatus.quantity,
      quantityFormatted: MathManager.formatNumberDecimal(objStatus.quantity),
      percentage: Object.prototype.hasOwnProperty.call(objStatus, 'percentage')
        ? objStatus.percentage
        : MathManager.percentageCalculator(
            objStatus.quantity,
            this.totalQuantity
          )
    }
  }

  mergeStatus (list) {
    const paidStatus = ['overpaid', 'paid', 'captured']
    const accPaidValues = []
    const quantityPaidValues = []

    const pendingStatus = ['pending', 'underpaid']
    const accPendingValues = []
    const quantityPendingValues = []

    list.forEach(element => {
      if (paidStatus.includes(element.name)) {
        accPaidValues.push(element.tpv)
        quantityPaidValues.push(element.quantity)
      }
      if (pendingStatus.includes(element.name)) {
        accPendingValues.push(element.tpv)
        quantityPendingValues.push(element.quantity)
      }
    })

    let filteredList = list.slice()

    if (accPaidValues.length > 0) {
      filteredList = filteredList.filter(item => {
        return !paidStatus.includes(item.name)
      })
      filteredList.splice(0, 0, {
        name: 'paid',
        tpv: accPaidValues.reduce((acc, curr) => acc + curr, 0),
        quantity: quantityPaidValues.reduce((acc, curr) => acc + curr, 0)
      })
    }

    if (accPendingValues.length > 0) {
      filteredList = filteredList.filter(item => {
        return !pendingStatus.includes(item.name)
      })
      filteredList.splice(filteredList.length, 0, {
        name: 'pending',
        tpv: accPendingValues.reduce((acc, curr) => acc + curr, 0),
        quantity: quantityPendingValues.reduce((acc, curr) => acc + curr, 0)
      })
    }

    return filteredList
  }

  static toString ({ quantityFormatted = 0, percentage = 0 }, label) {
    return `${quantityFormatted} ${label} (${percentage}%)`
  }

  static getFormatedLabelAndPercentage (
    { quantityFormatted = 0, percentage = 0 },
    label
  ) {
    return {
      label: `${quantityFormatted} ${label}`,
      percentage: `(${percentage}%)`
    }
  }
}

export default TransactionStatusManager
